// @ts-nocheck
import type { Question } from '../types';

/**
 * Pedestrian Crossings Questions (10 questions: 3 media + 7 text)
 * Official CBR 2026 & RVV 1990 Compliant
 * Based on: RVV 1990 Articles 5, 45, 49; CBR exam patterns
 */

export const pedestrianCrossingsQuestions: Question[] = [
  {
    id: 'q-pc-1',
    animationId: 'pc-anim-1',
    topic: 'General',
    question: 'You are driving and see a pedestrian waiting at a zebra crossing. What should you do?',
    options: [
      { id: 'q-pc-1o1', text: 'Stop and give way to the pedestrian' },
      { id: 'q-pc-1o2', text: 'Continue driving, pedestrians must wait' },
      { id: 'q-pc-1o3', text: 'Honk to warn the pedestrian' }
    ],
    answerId: 'q-pc-1o1',
    explanation: 'At zebra crossings, you must stop and give way to pedestrians who are waiting to cross or already crossing. (RVV 1990 art. 49)',
    subject: 'Pedestrian Crossings'
  },
  {
    id: 'q-pc-2',
    animationId: 'pc-anim-2',
    topic: 'General',
    question: 'You are driving and see a pedestrian crossing at a traffic light. The light is green for you. What should you do?',
    options: [
      { id: 'q-pc-2o1', text: 'Stop and give way to the pedestrian' },
      { id: 'q-pc-2o2', text: 'Continue driving, the light is green' },
      { id: 'q-pc-2o3', text: 'Honk to warn the pedestrian' }
    ],
    answerId: 'q-pc-2o1',
    explanation: 'Even when the light is green for you, you must stop and give way to pedestrians who are already crossing or about to cross.',
    subject: 'Pedestrian Crossings'
  },
  {
    id: 'q-pc-3',
    animationId: 'pc-anim-3',
    topic: 'General',
    question: 'You are driving and see a pedestrian crossing at a school crossing. What should you do?',
    options: [
      { id: 'q-pc-3o1', text: 'Stop and give way to the pedestrian' },
      { id: 'q-pc-3o2', text: 'Continue driving, school crossings are not mandatory' },
      { id: 'q-pc-3o3', text: 'Honk to warn the pedestrian' }
    ],
    answerId: 'q-pc-3o1',
    explanation: 'At school crossings, you must stop and give way to pedestrians, especially children, who are crossing or waiting to cross.',
    subject: 'Pedestrian Crossings'
  },
  {
    id: 'q-pc-4',
    topic: 'General',
    question: 'You are turning right at a junction and see a pedestrian crossing the road you are turning into. What should you do?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/pedestrian/q-pc-4-right-turn-pedestrian.jpg',
    options: [
      { id: 'q-pc-4o1', text: 'Stop and give way to the pedestrian' },
      { id: 'q-pc-4o2', text: 'Continue turning, pedestrians must wait' },
      { id: 'q-pc-4o3', text: 'Honk to warn the pedestrian' }
    ],
    answerId: 'q-pc-4o1',
    explanation: 'When turning at a junction, you must give way to pedestrians who are crossing the road you are turning into. (RVV 1990 art. 45)',
    subject: 'Pedestrian Crossings'
  },
  {
    id: 'q-pc-5',
    topic: 'General',
    question: 'Can you park your vehicle within 5 meters of a zebra crossing?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/pedestrian/q-pc-5-parking-prohibition.jpg',
    options: [
      { id: 'q-pc-5o1', text: 'Yes, if it is marked with white lines' },
      { id: 'q-pc-5o2', text: 'No, parking is prohibited within 5 meters of a crossing' },
      { id: 'q-pc-5o3', text: 'Yes, as long as no pedestrians are present' }
    ],
    answerId: 'q-pc-5o2',
    explanation: 'Parking is prohibited within 5 meters of a zebra crossing. This ensures that pedestrians have a clear view of approaching traffic and that drivers can see pedestrians approaching the crossing. (RVV 1990 art. 49)',
    subject: 'Pedestrian Crossings'
  },
  {
    id: 'q-pc-6',
    topic: 'General',
    question: 'What is a priority for a pedestrian on a zebra crossing?',
    options: [
      { id: 'q-pc-6o1', text: 'Pedestrians have no priority on a zebra crossing' },
      { id: 'q-pc-6o2', text: 'Pedestrians have absolute priority on a zebra crossing' },
      { id: 'q-pc-6o3', text: 'Priority depends on pedestrian age' }
    ],
    answerId: 'q-pc-6o2',
    explanation: 'Pedestrians have absolute priority on a zebra crossing. All drivers must stop and give way to pedestrians who are on or about to use the zebra crossing. (RVV 1990 art. 49)',
    subject: 'Pedestrian Crossings'
  },
  {
    id: 'q-pc-7',
    topic: 'General',
    question: 'Should you reduce speed when approaching a zebra crossing?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/pedestrian/q-pc-7-approach-crossing.jpg',
    options: [
      { id: 'q-pc-7o1', text: 'Only if pedestrians are visible' },
      { id: 'q-pc-7o2', text: 'Yes, always reduce speed and be prepared to stop' },
      { id: 'q-pc-7o3', text: 'No, maintain constant speed' }
    ],
    answerId: 'q-pc-7o2',
    explanation: 'You should always reduce speed when approaching a zebra crossing, even if no pedestrians are visible. Pedestrians may suddenly appear, so be prepared to stop at any time. (RVV 1990 art. 49)',
    subject: 'Pedestrian Crossings'
  },
  {
    id: 'q-pc-8',
    topic: 'General',
    question: 'Can you overtake another vehicle at a zebra crossing?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/pedestrian/q-pc-8-overtaking-prohibited.jpg',
    options: [
      { id: 'q-pc-8o1', text: 'Yes, if no pedestrians are present' },
      { id: 'q-pc-8o2', text: 'No, overtaking is prohibited at or near zebra crossings' },
      { id: 'q-pc-8o3', text: 'Yes, if the road is wide enough' }
    ],
    answerId: 'q-pc-8o2',
    explanation: 'Overtaking is prohibited at or near zebra crossings. This ensures pedestrian safety and prevents accidents. Wait until you have passed the crossing area before attempting to overtake. (RVV 1990 art. 49)',
    subject: 'Pedestrian Crossings'
  },
  {
    id: 'q-pc-9',
    topic: 'General',
    question: 'What should you do if traffic lights fail at a crossing that still has zebra markings?',
    options: [
      { id: 'q-pc-9o1', text: 'Treat it as a normal crossing and proceed' },
      { id: 'q-pc-9o2', text: 'Treat it as a zebra crossing and give way to pedestrians' },
      { id: 'q-pc-9o3', text: 'Stop and wait for lights to be repaired' }
    ],
    answerId: 'q-pc-9o2',
    explanation: 'If traffic lights fail but zebra markings are present, apply zebra-crossing rules and give way to pedestrians on or about to use the crossing. Approach at a speed that lets you stop safely. (RVV 1990 art. 49)',
    subject: 'Pedestrian Crossings'
  },
  {
    id: 'q-pc-10',
    topic: 'General',
    question: 'What is the difference between a zebra crossing and a pedestrian refuge island?',
    options: [
      { id: 'q-pc-10o1', text: 'They are the same thing' },
      { id: 'q-pc-10o2', text: 'A zebra crossing is on the road; a refuge island divides the road in two' },
      { id: 'q-pc-10o3', text: 'A refuge island is only for elderly people' }
    ],
    answerId: 'q-pc-10o2',
    explanation: 'A zebra crossing is a marked crossing area on the road where pedestrians cross in one stage. A pedestrian refuge island divides the road, allowing pedestrians to cross in two stages with a safe waiting area in the middle. (RVV 1990 art. 5)',
    subject: 'Pedestrian Crossings'
  }
];






